import React, {useState} from 'react';
import {Box, Button} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useQuery} from '@apollo/client';
import ActionModal from '../../components/ContactActions/ActionModal';
import AutoField from '../../components/AutoForm/AutoFieldNew';
import {GetFactFindTemplates} from '../../schema-types/GetFactFindTemplates';
import {getFactFindTemplates} from '../../components/ContactActions/SendFactFindModal';
import {SelectFactFindTemplate} from '../../components/ContactActions/SelectFactFindTemplate';
import {BulkAdminAlert, useActionsField, getisFactFind} from './BulkAdminModalHelpers';
import Loading from '../../components/Loading/Loading';
import {useBulkAdmin} from './useBulkAdmin';
import {BulkAdminValues} from './utils';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {GetBulkAccounts_bulkAccounts_edges_node} from '../../schema-types/GetBulkAccounts';
import {SaltusAny} from '../../utils/SaltusAny';
import {UPDATES} from '../../shared/UPDATES';

export default function BulkAdminModal({
  isOpen,
  close,
  bulkAccounts,
}: {
  isOpen: boolean;
  close: (event: SaltusAny, reason: 'backdropClick' | 'escapeKeyDown') => void;
  bulkAccounts: (GetBulkAccounts_bulkAccounts_edges_node | null | undefined)[];
}) {
  const {updateBulk} = useBulkAdmin();
  const {enqueueSnackbar} = useSnackbar();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const {data: templatesData} = useQuery<GetFactFindTemplates>(getFactFindTemplates);
  const [loading, setLoading] = useState(false);
  const {values, setFieldValue} = useAutoForm<BulkAdminValues>();
  const {selectedActions} = values;
  const isFactFind = getisFactFind(values);
  const actionsField = useActionsField();

  return (
    <ActionModal title="Bulk Admin Actions" isOpen={isOpen} close={close}>
      {loading ? <Loading /> : <AutoField field={actionsField} />}
      {isFactFind && (
        <Box mb={2}>
          <SelectFactFindTemplate
            selectedTemplateId={selectedTemplateId}
            setSelectedTemplateId={setSelectedTemplateId}
            templatesData={templatesData}
          />
        </Box>
      )}
      {!loading && <BulkAdminAlert bulkAccounts={bulkAccounts} values={values} />}
      <Box display="flex" justifyContent="end">
        <Button
          variant="outlined"
          color="primary"
          style={{marginRight: '1rem'}}
          onClick={() => close(null, 'escapeKeyDown')}
          disabled={!selectedActions.length || loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={!selectedActions.length || loading}
          onClick={() => {
            setLoading(true);
            updateBulk({
              variables: {
                idList: values.selectedIDs.map((rowId) => {
                  const contactId = bulkAccounts.find((acct) => {
                    return acct?.id === String(rowId);
                  })?.contactId;
                  if (!contactId) {
                    return null;
                  }
                  return contactId;
                }),
                actionList: values.selectedActions,
                additionalData: {
                  scope: 'Single',
                  templateId: selectedTemplateId,
                },
              },
            })
              .then((x) => {
                if (x.data?.bulkUpdateAccounts?.ok) {
                  enqueueSnackbar({
                    message: 'Action(s) sent',
                    variant: 'success',
                  });
                  return;
                }
                const err =
                  x.errors?.map((e) => e.message).join(',') ||
                  x.data?.bulkUpdateAccounts?.errors?.join(',') ||
                  'Error: action(s) not sent';
                enqueueSnackbar({
                  message: err,
                  variant: 'error',
                  autoHideDuration: null,
                });
              })
              .finally(() => {
                setLoading(false);
                /**
                 * actions list was getting stuck in a disabled state
                 * this makes the checklist enabled again for selection
                 * Only selectedActions is affected, selectedIDs retains its value
                 */
                setFieldValue(UPDATES._1211, 'selectedActions', []);
                close(null, 'escapeKeyDown');
              });
          }}
        >
          Run action(s)
        </Button>
      </Box>
    </ActionModal>
  );
}
